import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { ticketAPI } from '../api/client';
import { ArrowLeft, Trash2 } from 'lucide-react';

const statusOrder = ['new', 'in_progress', 'closed'];

export const AdminTicketsPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const response = await ticketAPI.getTickets();
      setTickets(response.data || []);
    } catch (err) {
      setError(err.response?.data?.error || t('ticketLoadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTickets(); }, []);

  const handleStatusChange = async (ticketId, status) => {
    try {
      await ticketAPI.updateTicketStatus(ticketId, status);
      setTickets((prev) => prev.map((ticket) => (ticket.id === ticketId ? { ...ticket, status } : ticket)));
    } catch (err) {
      setError(err.response?.data?.error || t('ticketStatusError'));
    }
  };

  const handleDeleteTicket = async (ticketId) => {
    if (!window.confirm(t('deleteTicketConfirm'))) return;
    try {
      await ticketAPI.deleteTicket(ticketId);
      setTickets((prev) => prev.filter((ticket) => ticket.id !== ticketId));
    } catch (err) {
      setError(err.response?.data?.error || t('ticketDeleteError'));
    }
  };

  return (
    <div className="ticket-page">
      <div className="ticket-container wide">
        <button onClick={() => navigate('/admin')} className="ticket-back-btn">
          <ArrowLeft size={16} />
          {t('back')}
        </button>

        <div className="ticket-card">
          <h1 className="ticket-page-title">{t('tickets')}</h1>
          <p className="ticket-page-desc">{t('ticketsAdminHint')}</p>

          {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

          {loading ? (
            <p style={{ color: 'rgba(245,244,240,0.5)', fontWeight: 600 }}>{t('loading')}</p>
          ) : tickets.length === 0 ? (
            <p style={{ color: 'rgba(245,244,240,0.4)', fontWeight: 600 }}>{t('ticketsEmpty')}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {tickets.map((ticket) => (
                <div key={ticket.id} className="ticket-item">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                      <div>
                        <div className="ticket-item-subject">{ticket.subject}</div>
                        <div className="ticket-item-meta">
                          {t('ticketFrom')}: {ticket.user?.name} ({ticket.user?.role})
                        </div>
                        <div className="ticket-item-date">
                          {new Date(ticket.createdAt).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                        <div>
                          <label className="form-label" style={{ fontSize: 11, marginBottom: 4 }}>{t('ticketStatus')}</label>
                          <select
                            value={ticket.status}
                            onChange={(e) => handleStatusChange(ticket.id, e.target.value)}
                            className="ticket-select"
                          >
                            {statusOrder.map((status) => (
                              <option key={status} value={status}>{t(`ticketStatus_${status}`)}</option>
                            ))}
                          </select>
                        </div>
                        <button onClick={() => handleDeleteTicket(ticket.id)} className="ticket-delete-btn">
                          <Trash2 size={14} />
                          {t('deleteTicket')}
                        </button>
                      </div>
                    </div>

                    <div className="ticket-item-desc">{ticket.description}</div>

                    {ticket.screenshot && (
                      <div style={{ paddingTop: 8 }}>
                        <img src={ticket.screenshot} alt="Ticket screenshot" style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 8, border: '1px solid rgba(255,255,255,0.08)' }} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
